import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loading, StarRating, ScoreRing, EmptyState } from "@/components/shared";
import { GitCompareArrows, Flag, X, ShieldCheck, MapPin } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function betterClass(a, b) {
  if (a == null || b == null || a === b) return "text-slate-200";
  return a > b ? "text-teal-400" : "text-slate-400";
}

function CompareRow({ label, a, b, render, higherIsBetter = true }) {
  const av = a, bv = b;
  const aCls = higherIsBetter ? betterClass(av, bv) : "text-slate-200";
  const bCls = higherIsBetter ? betterClass(bv, av) : "text-slate-200";
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4 border-b border-[#E4E1D8] py-4">
      <div className={`text-right font-medium ${aCls}`}>{render ? render(a) : (a ?? "-")}</div>
      <div className="min-w-[110px] text-center text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-left font-medium ${bCls}`}>{render ? render(b) : (b ?? "-")}</div>
    </div>
  );
}

function Picker({ label, companies, value, onChange, exclude, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-full min-w-0 border-[#DDDAD1] bg-ink-900">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent className="border-[#DDDAD1] bg-ink-900">
        {companies.filter((c) => c.id !== exclude).map((c) => (
          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Column({ c }) {
  return (
    <div className="text-center">
      <div className="mx-auto flex flex-col items-center">
        <ScoreRing value={c.avg_overall} />
        <h3 className="mt-3 text-lg font-semibold">{c.name}</h3>
        <p className="flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3 w-3" />{c.location || "-"}</p>
      </div>
    </div>
  );
}

export default function Compare() {
  const [params, setParams] = useSearchParams();
  const [companies, setCompanies] = useState(null);
  const [aId, setAId] = useState(params.get("a") || "");
  const [bId, setBId] = useState(params.get("b") || "");
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);

  useEffect(() => {
    api.get("/companies", { params: { sort: "name" } }).then(({ data }) => setCompanies(data)).catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    const p = {}; if (aId) p.a = aId; if (bId) p.b = bId; setParams(p, { replace: true });
    if (aId) api.get(`/companies/${aId}`).then(({ data }) => setA(data)).catch(() => setA(null)); else setA(null);
    if (bId) api.get(`/companies/${bId}`).then(({ data }) => setB(data)).catch(() => setB(null)); else setB(null);
    // eslint-disable-next-line
  }, [aId, bId]);

  if (companies === null) return <Loading label="Loading companies" />;

  return (
    <main className="mx-auto max-w-5xl px-5 py-12 lg:px-8">
      <p className="eyebrow mb-3 flex items-center gap-2"><GitCompareArrows className="h-4 w-4" /> Side-by-side</p>
      <h1 className="text-4xl font-bold">Compare companies</h1>
      <p className="mt-3 max-w-xl text-slate-400">Pick any two iGaming employers and weigh culture, pay, management and red flags at a glance.</p>

      <div className="mt-8 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4">
        <Picker label="Select company A" companies={companies} value={aId} onChange={setAId} exclude={bId} testid="compare-picker-a" />
        <span className="grid h-9 w-9 place-items-center rounded-full bg-teal-500/15 text-sm font-bold text-teal-400">VS</span>
        <Picker label="Select company B" companies={companies} value={bId} onChange={setBId} exclude={aId} testid="compare-picker-b" />
      </div>

      {!a || !b ? (
        <div className="mt-12">
          <EmptyState icon={GitCompareArrows} title="Choose two companies" subtitle="Select a company on each side to see the comparison." />
        </div>
      ) : (
        <div data-testid="compare-result" className="card-surface mt-10 p-6 sm:p-8 animate-fade-up">
          <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-4 pb-6">
            <Column c={a} />
            <div className="pt-6"><X className="h-5 w-5 text-slate-600" aria-hidden="true" /></div>
            <Column c={b} />
          </div>

          <CompareRow label="Overall" a={a.avg_overall} b={b.avg_overall} render={(v) => (
            <span className="inline-flex items-center gap-2 justify-self-auto"><StarRating value={v} size={13} />{v || "-"}</span>
          )} />
          <CompareRow label="Culture" a={a.avg_culture} b={b.avg_culture} render={(v) => v || "-"} />
          <CompareRow label="Pay" a={a.avg_pay} b={b.avg_pay} render={(v) => v || "-"} />
          <CompareRow label="Management" a={a.avg_management} b={b.avg_management} render={(v) => v || "-"} />
          <CompareRow label="Reviews" a={a.review_count} b={b.review_count} render={(v) => v ?? 0} />
          <CompareRow label="Red flags" a={a.red_flag_count} b={b.red_flag_count} higherIsBetter={false} render={(v) => (
            <span className={`inline-flex items-center gap-1.5 ${v > 0 ? "text-coral-500" : "text-slate-400"}`}><Flag className="h-3.5 w-3.5" />{v ?? 0}</span>
          )} />
          <CompareRow label="Licensing" a={a.licensing_status} b={b.licensing_status} higherIsBetter={false} render={(v) => (
            <span className="inline-flex items-center gap-1.5 text-sm"><ShieldCheck className="h-3.5 w-3.5 text-success-500" />{v}</span>
          )} />
          <CompareRow label="Remote" a={a.remote_culture} b={b.remote_culture} higherIsBetter={false} />
          <CompareRow label="Employment" a={a.employment_norm} b={b.employment_norm} higherIsBetter={false} />
        </div>
      )}
    </main>
  );
}
